import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { errorCopy } from '../../api/errors';
import { useIslandToday, useLocationId, useLocationTimezone } from '../../hooks/useSeedLocation';
import { businessDateIn, formatDate, formatMoney } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

interface Holiday {
  rowKey?: string;
  name: string;
  start: string;
  end: string;
  source: string;
}
interface Leave {
  id: string;
  kind: string;
  startDate: string;
  endDate: string;
  workingDates: string[];
  dailyPayMinor: number;
  employerDueMinor: number;
  ihssApprovedMinor: number;
  currency: string;
  cancelledAt: string | null;
  payBasisReference: string;
  certificateReference: string | null;
  createdAt: string;
  createdBy: string;
  note: string;
}
interface Payment {
  id: string;
  leaveId: string;
  payer: string;
  amountMinor: number;
  paidAt: string;
  paymentReference: string;
  acknowledgementReference: string;
  isAdvance: boolean;
}
interface Employee {
  staffId: string;
  staffName: string;
  hiredAt: string | null;
  balance: {
    profile: {
      effectiveFrom: string;
      ihssStatus: string;
      ordinaryHourlyRateMinor: number | null;
      currency: string;
    } | null;
    vacationDays: number | null;
    vacationUsedDays: number;
    vacationReservedDays: number;
    accruals: { date: string; days: number }[];
  };
  leaves: Leave[];
  payments: Payment[];
}
interface Benefits {
  ruleVersion: string;
  holidays: Holiday[];
  calendarNeedsReview: boolean;
  employees: Employee[];
}
const PATH = '/admin/v1/payroll';
const KINDS = [
  { value: 'vacation', label: 'Statutory vacation' },
  { value: 'paidLeave', label: 'Other paid leave' },
  { value: 'unpaidLeave', label: 'Leave of absence — unpaid' },
  { value: 'maternity', label: 'Maternity leave' },
  { value: 'pregnancyIncapacity', label: 'Certified pregnancy-related incapacity' },
  { value: 'pregnancyLoss', label: 'Certified pregnancy loss' },
];
const amount = (n: number | string) => Math.round(Number(n || 0) * 100);

export function BenefitsPanel() {
  const locationId = useLocationId(),
    today = useIslandToday(),
    timezone = useLocationTimezone();
  const cache = useQueryClient();
  const [staffId, setStaffId] = useState<string | null>(null),
    [dialog, setDialog] = useState<'profile' | 'leave' | 'payment' | 'calendar' | 'cancel' | null>(null),
    [leaveId, setLeaveId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['benefits', locationId, today],
    queryFn: () =>
      rawApi<Benefits>(
        'GET',
        `${PATH}/benefits?locationId=${encodeURIComponent(locationId ?? '')}&asOf=${today}`,
      ),
    enabled: !!locationId,
  });
  const employee = query.data?.employees.find((e) => e.staffId === staffId);
  const leave = employee?.leaves.find((l) => l.id === leaveId);
  const [kind, setKind] = useState('vacation'),
    [start, setStart] = useState(today),
    [end, setEnd] = useState(today),
    [dates, setDates] = useState<string[]>([]),
    [daily, setDaily] = useState<number | string>(0),
    [currency, setCurrency] = useState('HNL'),
    [basis, setBasis] = useState(''),
    [note, setNote] = useState(''),
    [certificate, setCertificate] = useState(''),
    [due, setDue] = useState(''),
    [birth, setBirth] = useState(''),
    [subsidy, setSubsidy] = useState<number | string>(0),
    [subsidyRef, setSubsidyRef] = useState('');
  const [opening, setOpening] = useState<number | string>(0),
    [rate, setRate] = useState<number | string>(''),
    [coverage, setCoverage] = useState('unknown'),
    [baseline, setBaseline] = useState(today);
  const [payer, setPayer] = useState('employer'),
    [paymentAmount, setPaymentAmount] = useState<number | string>(0),
    [paidDate, setPaidDate] = useState(today),
    [receipt, setReceipt] = useState(''),
    [acknowledgement, setAcknowledgement] = useState(''),
    [run, setRun] = useState(''),
    [advance, setAdvance] = useState(false);
  const [windows, setWindows] = useState<Holiday[]>([]),
    [calendarSource, setCalendarSource] = useState(''),
    [calendarYear, setCalendarYear] = useState<number | string>(Number(today.slice(0, 4)));
  const mutation = useMutation({
    mutationFn: ({ path, body }: { path: string; body: unknown }) => rawApi('POST', path, body),
    onSuccess: () => {
      setDialog(null);
      notifyDone('Record saved', 'Leave, benefit or payment evidence is now in the history.');
      void cache.invalidateQueries({ queryKey: ['benefits'] });
      void cache.invalidateQueries({ queryKey: ['list', `${PATH}/timesheets`] });
    },
    onError: (error) => notifyError(error),
  });
  const save = () => {
    if (dialog === 'cancel' && leave)
      mutation.mutate({ path: `${PATH}/leave/${leave.id}/cancel`, body: { reason: note } });
    if (dialog === 'profile' && employee)
      mutation.mutate({
        path: `${PATH}/staff/${employee.staffId}/benefit-profile`,
        body: {
          effectiveFrom: baseline,
          openingVacationDays: Number(opening),
          ordinaryHourlyRateMinor: rate === '' ? null : amount(rate),
          currency,
          ihssStatus: coverage,
          evidenceReference: basis,
        },
      });
    if (dialog === 'leave' && employee) {
      const selected = [...dates].sort();
      mutation.mutate({
        path: `${PATH}/leave`,
        body: {
          staffId: employee.staffId,
          kind,
          startDate: kind === 'vacation' ? (selected[0] ?? start) : start,
          endDate: kind === 'vacation' ? (selected.at(-1) ?? end) : end,
          workingDates: kind === 'vacation' ? selected : [],
          dailyPayMinor: kind === 'unpaidLeave' ? 0 : amount(daily),
          currency,
          payBasisReference: basis,
          note,
          certificateReference: certificate || undefined,
          dueDate: due || undefined,
          birthDate: birth || undefined,
          ihssApprovedMinor: amount(subsidy),
          ihssApprovalReference: subsidyRef || undefined,
        },
      });
    }
    if (dialog === 'payment' && leave)
      mutation.mutate({
        path: `${PATH}/leave/${leave.id}/payments`,
        body: {
          payer,
          amountMinor: amount(paymentAmount),
          currency: leave.currency,
          paidAt: paidDate === today ? new Date().toISOString() : `${paidDate}T12:00:00-06:00`,
          paymentReference: receipt,
          acknowledgementReference: acknowledgement,
          payrollRunId: run || undefined,
          isAdvance: advance,
        },
      });
    if (dialog === 'calendar')
      mutation.mutate({
        path: `${PATH}/holiday-calendars`,
        body: { locationId, year: Number(calendarYear), windows, sourceReference: calendarSource },
      });
  };
  const resetLeave = () => {
    setKind('vacation');
    setStart(today);
    setEnd(today);
    setDates([]);
    setDaily(0);
    setBasis('');
    setNote('');
    setCertificate('');
    setDue('');
    setBirth('');
    setSubsidy(0);
    setSubsidyRef('');
    setCurrency(employee?.balance.profile?.currency ?? 'HNL');
    setDialog('leave');
  };
  const loadError = query.error ? errorCopy(query.error) : null;
  return (
    <Stack>
      <Title order={3}>Vacation, leave &amp; holidays</Title>
      <Text size="sm">
        Statutory vacation vests after each completed service year: 10, 12, 15, then 20 working days. An
        opening balance reconciles earlier leave; time off does not reset service. Payments and signed records
        are recorded separately.
      </Text>
      {loadError && (
        <Alert color="red" title={loadError.title}>
          {loadError.message}
        </Alert>
      )}
      <Group align="end">
        <Select
          label="Employee"
          placeholder="Select an employee"
          data={query.data?.employees.map((e) => ({ value: e.staffId, label: e.staffName })) ?? []}
          value={staffId}
          onChange={setStaffId}
          searchable
        />
        <Button disabled={!employee} onClick={resetLeave}>
          Record days off
        </Button>
        <Button
          variant="light"
          disabled={!employee}
          onClick={() => {
            setBasis('');
            setBaseline(today);
            setOpening(employee?.balance.vacationDays ?? 0);
            setCoverage(employee?.balance.profile?.ihssStatus ?? 'unknown');
            setCurrency(employee?.balance.profile?.currency ?? 'HNL');
            setRate(
              employee?.balance.profile?.ordinaryHourlyRateMinor
                ? employee.balance.profile.ordinaryHourlyRateMinor / 100
                : '',
            );
            setDialog('profile');
          }}
        >
          Benefit coverage &amp; opening balance
        </Button>
      </Group>
      {employee && (
        <>
          <Paper withBorder p="md">
            <Group>
              <Text fw={600}>{employee.staffName}</Text>
              <Badge color={employee.balance.profile?.ihssStatus === 'enrolled' ? 'green' : 'orange'}>
                IHSS:{' '}
                {employee.balance.profile?.ihssStatus === 'enrolled'
                  ? 'Verified enrolled'
                  : employee.balance.profile?.ihssStatus === 'uncovered'
                    ? 'Not covered'
                    : 'Not confirmed'}
              </Badge>
            </Group>
            <Text>
              {employee.balance.vacationDays === null
                ? 'Opening balance and hire date need reconciliation'
                : `${employee.balance.vacationDays} vested days remaining · ${employee.balance.vacationUsedDays} used since opening · ${employee.balance.vacationReservedDays} reserved for future leave`}
            </Text>
            <Text size="sm">
              Hire date: {employee.hiredAt?.slice(0, 10) ?? 'missing'} · Opening date:{' '}
              {employee.balance.profile?.effectiveFrom ?? 'not recorded'}
            </Text>
            {employee.balance.accruals.map((a) => (
              <Text key={a.date} size="sm">
                {a.date}: {a.days} days earned
              </Text>
            ))}
          </Paper>
          {employee.balance.profile?.ihssStatus !== 'enrolled' && (
            <Alert color="orange">
              Confirm IHSS registration and contribution history. An unapproved subsidy does not reduce
              employer maternity pay. This record does not calculate registration arrears.
            </Alert>
          )}
          <Text size="sm">
            Vacation pay uses the ordinary daily average over the prior six months, including benefits in
            kind. Record the wage calculation and signed leave acknowledgement in the personnel file. Vacation
            wages are due three days before leave begins; notice is normally ten days before.
          </Text>
          <Table.ScrollContainer minWidth={720}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Leave</Table.Th>
                  <Table.Th>Days taken</Table.Th>
                  <Table.Th>Employer owed / documented paid</Table.Th>
                  <Table.Th>IHSS approved / documented paid</Table.Th>
                  <Table.Th>Records</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {employee.leaves.map((l) => {
                  const paid = (who: string) =>
                    employee.payments
                      .filter((p) => p.leaveId === l.id && p.payer === who)
                      .reduce((a, p) => a + p.amountMinor, 0);
                  return (
                    <Table.Tr key={l.id}>
                      <Table.Td>
                        {KINDS.find((k) => k.value === l.kind)?.label}
                        <Text size="sm">
                          {l.startDate} — {l.endDate}
                        </Text>
                        {l.cancelledAt && <Badge color="gray">Cancelled</Badge>}
                      </Table.Td>
                      <Table.Td>
                        {l.workingDates.length ? l.workingDates.join(', ') : 'Calendar absence'}
                        <Text size="xs">{l.payBasisReference}</Text>
                      </Table.Td>
                      <Table.Td>
                        {formatMoney(l.employerDueMinor, l.currency)} /{' '}
                        {formatMoney(paid('employer'), l.currency)}
                        <Text size="xs">
                          Balance {formatMoney(l.employerDueMinor - paid('employer'), l.currency)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {formatMoney(l.ihssApprovedMinor, l.currency)} /{' '}
                        {formatMoney(paid('ihss'), l.currency)}
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          disabled={!!l.cancelledAt}
                          onClick={() => {
                            setLeaveId(l.id);
                            setPaymentAmount((l.employerDueMinor - paid('employer')) / 100);
                            setPaidDate(today);
                            setReceipt('');
                            setAcknowledgement('');
                            setRun('');
                            setAdvance(false);
                            setPayer('employer');
                            setDialog('payment');
                          }}
                        >
                          Record payment evidence
                        </Button>
                        {!l.cancelledAt && !employee.payments.some((p) => p.leaveId === l.id) && (
                          <Button
                            size="xs"
                            variant="subtle"
                            onClick={() => {
                              setLeaveId(l.id);
                              setNote('');
                              setDialog('cancel');
                            }}
                          >
                            Cancel with a reason
                          </Button>
                        )}
                        {employee.payments
                          .filter((p) => p.leaveId === l.id)
                          .map((p) => (
                            <Text size="xs" key={p.id}>
                              {formatDate(businessDateIn(timezone, 0, new Date(p.paidAt)))} ·{' '}
                              {p.paymentReference} · Signed record: {p.acknowledgementReference}
                              {p.isAdvance ? ' · Advance' : ''}
                            </Text>
                          ))}
                        <Text size="xs">
                          Recorded {formatDate(businessDateIn(timezone, 0, new Date(l.createdAt)))}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}
      <Title order={4}>Private-sector public holidays · {today.slice(0, 4)}</Title>
      {query.data?.calendarNeedsReview && (
        <Alert color="orange">
          This year has an exceptional calendar. Record the official private-sector notice before holiday pay
          can be finalized.
        </Alert>
      )}
      <Text size="sm">
        Payroll adds the holiday premium to ordinary pay for the hours actually worked, including overnight
        shifts and Morazánica noon boundaries. Approved hours preserve the holiday calculation. Unclear shift
        times or a missing ordinary hourly rate require review.
      </Text>
      <Table>
        <Table.Tbody>
          {query.data?.holidays.map((h) => (
            <Table.Tr key={h.start}>
              <Table.Td>{h.name}</Table.Td>
              <Table.Td>
                {h.start.replace('T', ' ')} — {h.end.replace('T', ' ')} (exclusive end)
              </Table.Td>
              <Table.Td>
                <a href={h.source} target="_blank" rel="noreferrer">
                  Official source
                </a>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Button
        variant="subtle"
        onClick={() => {
          setWindows((query.data?.holidays ?? []).map((w) => ({ ...w, rowKey: crypto.randomUUID() })));
          setCalendarSource('');
          setCalendarYear(Number(today.slice(0, 4)));
          setDialog('calendar');
        }}
      >
        Record an official calendar exception
      </Button>
      <Modal
        opened={dialog !== null}
        onClose={() => setDialog(null)}
        title={
          dialog === 'profile'
            ? 'Reconcile benefits'
            : dialog === 'leave'
              ? 'Record time off'
              : dialog === 'payment'
                ? 'Payment evidence'
                : dialog === 'cancel'
                  ? 'Cancel an unpaid leave record'
                  : 'Official holiday calendar'
        }
        size="lg"
      >
        <Stack>
          {dialog === 'cancel' && (
            <>
              <Text size="sm">
                Cancellation restores unused vacation days and keeps the original record in history. Paid or
                approved payroll records require a correction.
              </Text>
              <TextInput
                label="Reason for correction"
                value={note}
                onChange={(e) => setNote(e.currentTarget.value)}
                required
              />
            </>
          )}
          {dialog === 'profile' && (
            <>
              <Text size="sm">
                Verify prior vacation taken and paid before entering the remaining opening days. Later
                coverage or rate changes preserve the calculated balance at the new date.
              </Text>
              <TextInput
                type="date"
                label="Balance date"
                value={baseline}
                onChange={(e) => setBaseline(e.currentTarget.value)}
              />
              <NumberInput
                label="Unused statutory vacation days on this date"
                value={opening}
                onChange={setOpening}
                min={0}
                allowDecimal={false}
              />
              <Select
                label="IHSS coverage"
                value={coverage}
                onChange={(v) => setCoverage(v ?? 'unknown')}
                data={[
                  { value: 'unknown', label: 'Not confirmed' },
                  { value: 'uncovered', label: 'Not currently covered — registration review needed' },
                  { value: 'enrolled', label: 'Verified enrolled' },
                ]}
              />
              <NumberInput
                label="Verified ordinary hourly rate (for salary holiday work)"
                description="Leave blank until the legal wage basis is verified"
                value={rate}
                onChange={setRate}
                min={0}
                decimalScale={2}
              />
              <Select
                label="Currency"
                data={['HNL', 'USD']}
                value={currency}
                onChange={(v) => setCurrency(v ?? 'HNL')}
              />
              <TextInput
                label="Balance, coverage and wage evidence reference"
                value={basis}
                onChange={(e) => setBasis(e.currentTarget.value)}
                required
              />
            </>
          )}
          {dialog === 'leave' && (
            <>
              <Select
                label="Type of leave"
                data={KINDS}
                value={kind}
                onChange={(v) => setKind(v ?? 'vacation')}
              />
              {kind === 'vacation' ? (
                <DatePickerInput
                  type="multiple"
                  label="Working days actually taken"
                  description="Select scheduled working days, excluding rest and public holidays. Notify vacation dates at least 10 days ahead and record vacation pay at least 3 days before the leave begins."
                  value={dates}
                  onChange={setDates}
                />
              ) : (
                <Group grow>
                  <TextInput
                    type="date"
                    label="First absent day"
                    value={start}
                    onChange={(e) => setStart(e.currentTarget.value)}
                  />
                  <TextInput
                    type="date"
                    label="Last absent day"
                    value={end}
                    onChange={(e) => setEnd(e.currentTarget.value)}
                  />
                </Group>
              )}
              {kind !== 'unpaidLeave' && (
                <NumberInput
                  label="Verified daily benefit wage"
                  description={
                    kind === 'vacation'
                      ? 'Ordinary six-month daily average, including benefits in kind'
                      : 'Contractual daily wage; variable maternity wages use the prior-year average'
                  }
                  value={daily}
                  onChange={setDaily}
                  min={0}
                  decimalScale={2}
                />
              )}
              <Select
                label="Currency"
                data={['HNL', 'USD']}
                value={currency}
                onChange={(v) => setCurrency(v ?? 'HNL')}
              />
              <TextInput
                label="Wage calculation / agreement reference"
                value={basis}
                onChange={(e) => setBasis(e.currentTarget.value)}
                required
              />
              {['maternity', 'pregnancyLoss', 'pregnancyIncapacity'].includes(kind) && (
                <>
                  <TextInput
                    label="Medical certificate reference"
                    description="Keep medical details in the protected personnel file"
                    value={certificate}
                    onChange={(e) => setCertificate(e.currentTarget.value)}
                  />
                  {kind === 'maternity' && (
                    <>
                      <TextInput
                        type="date"
                        label="Certified expected delivery date"
                        value={due}
                        onChange={(e) => setDue(e.currentTarget.value)}
                      />
                      <TextInput
                        type="date"
                        label="Actual birth date, when known"
                        value={birth}
                        onChange={(e) => setBirth(e.currentTarget.value)}
                      />
                      <Alert>
                        Maternity covers four weeks before and six after birth under the Labour Code;
                        confirmed IHSS coverage uses its six-week prenatal window. Certificate dates control
                        early or late births. Nursing breaks remain paid after return; they are not vacation.
                      </Alert>
                    </>
                  )}
                  <NumberInput
                    label="IHSS subsidy actually approved for this leave"
                    description="Use zero until verified; employer pays the full wage less the confirmed subsidy"
                    value={subsidy}
                    onChange={setSubsidy}
                    min={0}
                  />
                  <TextInput
                    label="IHSS approval reference"
                    value={subsidyRef}
                    onChange={(e) => setSubsidyRef(e.currentTarget.value)}
                  />
                </>
              )}
              <Textarea
                label="Reason / agreement and signed leave record reference"
                value={note}
                onChange={(e) => setNote(e.currentTarget.value)}
                required
              />
              <Text size="sm">
                This creates linked time-off entries. Rebuild and review affected hours and payroll. The
                benefit ledger records what is owed; a leave entry alone is not evidence of payment.
              </Text>
            </>
          )}
          {dialog === 'payment' && (
            <>
              <Select
                label="Paid by"
                data={[
                  { value: 'employer', label: 'Employer' },
                  { value: 'ihss', label: 'IHSS' },
                ]}
                value={payer}
                onChange={(v) => setPayer(v ?? 'employer')}
              />
              <NumberInput
                label={`Amount actually paid (${leave?.currency})`}
                value={paymentAmount}
                onChange={setPaymentAmount}
                min={0}
                decimalScale={2}
              />
              <TextInput
                type="date"
                label="Payment date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.currentTarget.value)}
              />
              <TextInput
                label="Receipt / cash voucher / transfer reference"
                value={receipt}
                onChange={(e) => setReceipt(e.currentTarget.value)}
                required
              />
              <TextInput
                label="Employee signature, fingerprint or witness record reference"
                value={acknowledgement}
                onChange={(e) => setAcknowledgement(e.currentTarget.value)}
                required
              />
              <TextInput
                label="Paid payroll register ID, if included in payroll"
                value={run}
                onChange={(e) => setRun(e.currentTarget.value)}
              />
              <Checkbox
                label="Separate advance to deduct from future payroll for these leave days"
                checked={advance}
                onChange={(e) => setAdvance(e.currentTarget.checked)}
              />
              <Text size="sm">
                A payment already included in ordinary payroll is a receipt, not an advance. Keep the
                underlying signed documents; recording a reference does not replace them.
              </Text>
            </>
          )}
          {dialog === 'calendar' && (
            <>
              <Text size="sm">
                This replaces the complete calendar for {calendarYear}. Retain all statutory holidays and use
                an authoritative private-sector decree. Keep the existing holiday labels and their full paid
                duration; missing holidays cannot be saved. Reopen affected hours first.
              </Text>
              <NumberInput
                label="Calendar year"
                min={2000}
                max={2100}
                allowDecimal={false}
                value={calendarYear}
                onChange={(v) => {
                  setCalendarYear(v);
                  setWindows(
                    Number(v) === Number(today.slice(0, 4))
                      ? (query.data?.holidays ?? []).map((w) => ({ ...w, rowKey: crypto.randomUUID() }))
                      : [],
                  );
                }}
              />
              <TextInput
                label="Official notice reference"
                value={calendarSource}
                onChange={(e) => setCalendarSource(e.currentTarget.value)}
              />
              {windows.map((w, i) => (
                <Paper key={w.rowKey} withBorder p="sm">
                  <TextInput
                    label="Holiday"
                    value={w.name}
                    onChange={(e) =>
                      setWindows(windows.map((v, j) => (j === i ? { ...v, name: e.currentTarget.value } : v)))
                    }
                  />
                  <Group grow>
                    <TextInput
                      type="datetime-local"
                      label="Starts (Honduras local time)"
                      value={w.start}
                      onChange={(e) =>
                        setWindows(
                          windows.map((v, j) => (j === i ? { ...v, start: e.currentTarget.value } : v)),
                        )
                      }
                    />
                    <TextInput
                      type="datetime-local"
                      label="Ends (exclusive)"
                      value={w.end}
                      onChange={(e) =>
                        setWindows(
                          windows.map((v, j) => (j === i ? { ...v, end: e.currentTarget.value } : v)),
                        )
                      }
                    />
                  </Group>
                  <TextInput
                    label="Source URL"
                    value={w.source}
                    onChange={(e) =>
                      setWindows(
                        windows.map((v, j) => (j === i ? { ...v, source: e.currentTarget.value } : v)),
                      )
                    }
                  />
                  <Button
                    color="red"
                    variant="subtle"
                    size="xs"
                    onClick={() => setWindows(windows.filter((_, j) => j !== i))}
                  >
                    Remove
                  </Button>
                </Paper>
              ))}
              <Button
                variant="light"
                onClick={() =>
                  setWindows([
                    ...windows,
                    {
                      rowKey: crypto.randomUUID(),
                      name: '',
                      start: `${calendarYear}-01-01T00:00`,
                      end: `${calendarYear}-01-02T00:00`,
                      source: '',
                    },
                  ])
                }
              >
                Add holiday interval
              </Button>
            </>
          )}
          <Group justify="end">
            <Button variant="default" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button loading={mutation.isPending} onClick={save}>
              Save record
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
